import { useRef } from "react";
import { ArrowRight, ClipboardList, GraduationCap, Lock, Sparkles, Activity } from "lucide-react";
import Button from "../foundational/Button";
import { HERRAMIENTAS, MOMENTOS, herramientaRequiereCuenta } from "../../lib/presentacionMiPAE";
import { colorModulo } from "./colorModulo";
import { VinetaMomento } from "./Vinetas";

// Los cuatro momentos de enfermería en que MiPAE acompaña. La portada los
// muestra como pestañas (una a la vez, para que la página no se alargue) y
// «¿Qué es MiPAE?» como capítulos seguidos. El texto de cada momento y su
// viñeta son los mismos en las dos superficies: un solo lugar que corregir.

export const ICONO_MOMENTO = {
  aprender: GraduationCap,
  fundamentar: Sparkles,
  turno: ClipboardList,
  consultar: Activity,
};

// Herramientas del momento con su disponibilidad real (lib/accessGate). Una
// grilla liviana, sin cajas: el nombre manda y la disponibilidad va debajo.
function HerramientasDelMomento({ ids }) {
  return (
    <ul className="grid grid-cols-2 gap-x-4 gap-y-2.5 sm:grid-cols-3 sm:gap-y-3" aria-label="Herramientas">
      {ids.map((id) => {
        const h = HERRAMIENTAS[id];
        const c = colorModulo(h.modulo);
        const conCuenta = herramientaRequiereCuenta(id);
        return (
          <li key={id} className="flex min-w-0 items-start gap-2">
            <span aria-hidden="true" className={`mt-1.5 h-2 w-2 flex-shrink-0 rounded-full ${c.punto}`} />
            <span className="min-w-0">
              <span className="block text-small font-semibold text-ink">{h.nombre}</span>
              <span className="block text-small font-normal text-ink-subtle dark:text-ink-muted">{conCuenta ? "Con cuenta" : "Sin cuenta"}</span>
            </span>
          </li>
        );
      })}
    </ul>
  );
}

// Texto de un momento. `acciones`: [{ label, onClick, primaria }]. La
// acción primaria usa el botón primario del sistema y el resto va como
// enlace de texto al lado: dos botones grandes apilados pesaban más que el
// contenido en el teléfono. Una sola CTA primaria por vista (principio 5).
// Sin números ni cita en cursiva (pasada "menos Claude", 2026-09-29): la
// escena va como primera frase normal, en tinta plena, y sigue lo que hace
// MiPAE. En los capítulos, el ícono del módulo reemplaza al "01 ·".
export function MomentoTexto({ momento, acciones = [], conIcono = false, nivelTitulo = "h3" }) {
  const Titulo = nivelTitulo;
  const c = colorModulo(momento.modulo);
  const Icono = ICONO_MOMENTO[momento.id];
  return (
    <div>
      {conIcono && (
        <span aria-hidden="true" className={`mb-3 grid h-10 w-10 place-items-center rounded-tk-md sm:mb-4 sm:h-11 sm:w-11 ${c.tinte} ${c.icono}`}>
          <Icono size={20} strokeWidth={1.8} />
        </span>
      )}
      <Titulo className="text-20px font-semibold leading-tight tracking-tight text-ink sm:text-26px">
        {momento.titulo}
      </Titulo>
      <p className="mt-3 text-body leading-relaxed sm:mt-4">
        <span className="font-medium text-ink">{momento.situacion}</span>{" "}
        <span className="text-ink-subtle dark:text-ink-muted">{momento.texto}</span>
      </p>
      {momento.nota && <p className="mt-3 text-body leading-relaxed text-ink-subtle dark:text-ink-muted">{momento.nota}</p>}
      <div className="mt-5 border-t border-edge pt-4 sm:mt-6">
        <HerramientasDelMomento ids={momento.herramientas} />
      </div>
      {acciones.length > 0 && (
        <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-1">
          {acciones.map((a) => (a.primaria ? (
            <Button key={a.label} variant="primary" size="lg" onClick={a.onClick} className="min-h-[44px]">
              {a.label}
            </Button>
          ) : (
            <button
              key={a.label}
              type="button"
              onClick={a.onClick}
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-tk-sm text-small font-semibold text-accent dark:text-accent-light hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
            >
              {a.conCuenta && <Lock aria-hidden="true" size={13} strokeWidth={2} />}
              {a.label}
              {!a.conCuenta && <ArrowRight aria-hidden="true" size={15} strokeWidth={2} />}
            </button>
          )))}
        </div>
      )}
    </div>
  );
}

// Pestañas accesibles (patrón tablist de WAI-ARIA): flechas, Inicio y Fin
// mueven la selección; solo la pestaña activa entra al orden de tabulación.
export function MomentosPestanas({ activo, onCambiar, accionesDe }) {
  const refs = useRef({});
  const idx = Math.max(0, MOMENTOS.findIndex((m) => m.id === activo));
  const momento = MOMENTOS[idx];

  const alTeclado = (e) => {
    const n = MOMENTOS.length;
    let destino = null;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") destino = (idx + 1) % n;
    else if (e.key === "ArrowLeft" || e.key === "ArrowUp") destino = (idx - 1 + n) % n;
    else if (e.key === "Home") destino = 0;
    else if (e.key === "End") destino = n - 1;
    if (destino == null) return;
    e.preventDefault();
    const id = MOMENTOS[destino].id;
    onCambiar(id);
    refs.current[id]?.focus();
  };

  return (
    <div>
      {/* En el teléfono las pestañas van solo con su texto: con ícono eran
          cuatro tarjetas gruesas que ocupaban media pantalla antes de
          mostrar nada. El ícono vuelve en escritorio. */}
      <div role="tablist" aria-label="Momentos en que MiPAE te acompaña" className="grid grid-cols-2 gap-2 lg:grid-cols-4">
        {MOMENTOS.map((m) => {
          const sel = m.id === momento.id;
          const c = colorModulo(m.modulo);
          const Icono = ICONO_MOMENTO[m.id];
          return (
            <button
              key={m.id}
              ref={(el) => { refs.current[m.id] = el; }}
              type="button"
              role="tab"
              id={`momento-tab-${m.id}`}
              aria-selected={sel}
              aria-controls={`momento-panel-${m.id}`}
              tabIndex={sel ? 0 : -1}
              onClick={() => onCambiar(m.id)}
              onKeyDown={alTeclado}
              className={[
                "flex min-h-[48px] items-center gap-2 rounded-tk-md border px-3.5 py-2 text-left transition-colors lg:min-h-[60px] lg:gap-2.5 lg:py-2.5",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 focus-visible:ring-offset-2 focus-visible:ring-offset-paper",
                sel ? "border-accent bg-paper ring-1 ring-accent" : "border-edge bg-paper hover:border-ink-muted/50",
              ].join(" ")}
            >
              <span aria-hidden="true" className={`hidden h-9 w-9 flex-shrink-0 place-items-center rounded-tk-sm lg:grid ${c.tinte} ${c.icono}`}>
                <Icono size={18} strokeWidth={1.8} />
              </span>
              <span className={`text-small leading-snug ${sel ? "font-semibold text-ink" : "font-medium text-ink-subtle dark:text-ink-muted"}`}>
                {m.pestana}
              </span>
            </button>
          );
        })}
      </div>

      <div
        key={momento.id}
        role="tabpanel"
        id={`momento-panel-${momento.id}`}
        aria-labelledby={`momento-tab-${momento.id}`}
        tabIndex={0}
        className="mt-7 grid grid-cols-1 items-start gap-8 animate-cruce focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 focus-visible:ring-offset-4 focus-visible:ring-offset-paper lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)] lg:gap-14"
      >
        <MomentoTexto momento={momento} acciones={accionesDe(momento.id)} />
        <VinetaMomento id={momento.vineta} />
      </div>
    </div>
  );
}

// Capítulos seguidos, alternando el lado de la viñeta en escritorio para que
// la lectura tenga ritmo y no sea una columna de tarjetas iguales.
export function MomentosCapitulos({ accionesDe }) {
  return (
    <div className="space-y-16 sm:space-y-20 lg:space-y-28">
      {MOMENTOS.map((m, i) => (
        <article
          key={m.id}
          id={`momento-${m.id}`}
          className="pp-aparece grid scroll-mt-24 grid-cols-1 items-start gap-8 lg:grid-cols-2 lg:gap-16"
        >
          <div className={`min-w-0${i % 2 === 1 ? " lg:order-2" : ""}`}>
            <MomentoTexto momento={m} conIcono acciones={accionesDe(m.id)} />
          </div>
          <div className={`min-w-0${i % 2 === 1 ? " lg:order-1" : ""}`}>
            <VinetaMomento id={m.vineta} conSemana={m.id === "turno"} />
          </div>
        </article>
      ))}
    </div>
  );
}
